import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import SEOHead from './SEOHead';
import { 
  ArrowRight, 
  Phone, 
  CheckCircle,
  Building,
  Scale,
  Stethoscope,
  Calculator,
  Factory,
  ShoppingBag,
  Home,
  Heart,
  GraduationCap,
  Landmark,
  Shield,
  Clock,
  Users,
  Server,
  Cloud,
  Lock,
  FileText
} from 'lucide-react';

// Redirects from old industry pages to new dedicated landing pages
const INDUSTRY_REDIRECTS = {
  'law-firms': '/law-firm-it-support-toronto',
  'medical-clinics': '/medical-practice-it-support-toronto',
  'accounting-firms': '/real-estate-accounting-it-support-toronto',
  'real-estate': '/real-estate-accounting-it-support-toronto'
};

const industryData = {
  'law-firms': {
    title: 'Law Firms',
    icon: Scale,
    heroImage: '/images/optimized/pexels-5668858.webp',
    description: 'Specialized IT solutions for law firms and legal practices across the GTA.',
    intro: 'Law firms handle sensitive client data and require robust, secure IT infrastructure. King of IT understands the unique technology needs of legal practices, from document management systems to client confidentiality requirements.',
    challenges: [
      'Protecting confidential client information and attorney-client privilege',
      'Managing large volumes of legal documents and case files',
      'Ensuring compliance with Law Society regulations',
      'Maintaining secure communication with clients and courts',
      'Supporting remote work for lawyers and staff',
      'Integrating practice management software'
    ],
    solutions: [
      'Encrypted email and secure file sharing systems',
      'Document management system implementation',
      'Legal practice management software support',
      'Secure remote access for attorneys',
      'Data backup and disaster recovery planning',
      'Compliance auditing and reporting'
    ],
    compliance: ['Law Society of Ontario requirements', 'Privacy regulations', 'E-discovery compliance'],
    cta: 'Protect your firm and clients with enterprise-grade IT security.'
  },
  'medical-clinics': {
    title: 'Medical Clinics',
    icon: Stethoscope,
    heroImage: '/images/optimized/pexels-4386467.webp',
    description: 'PHIPA-compliant IT services for medical clinics and healthcare providers in Ontario.',
    intro: 'Healthcare providers must balance patient care with strict data privacy requirements. King of IT provides specialized IT support for medical clinics, ensuring your systems are secure, compliant with PHIPA, and always available when patients need care.',
    challenges: [
      'PHIPA and PIPEDA compliance for patient data',
      'EMR/EHR system implementation and support',
      'Secure integration with Ontario Health networks',
      'Protecting patient records from cyber threats',
      'Maintaining uptime for critical healthcare systems',
      'Supporting telehealth and virtual care'
    ],
    solutions: [
      'EMR system setup, migration, and support',
      'PHIPA-compliant data backup and recovery',
      'Secure patient portal implementation',
      'Medical device network integration',
      'Telehealth platform support',
      '24/7 monitoring for critical systems'
    ],
    compliance: ['PHIPA', 'PIPEDA', 'Ontario Health requirements'],
    cta: 'Keep your clinic running and your patients\' data secure.'
  },
  'dental-offices': {
    title: 'Dental Offices',
    icon: Stethoscope,
    heroImage: '/images/optimized/pexels-3845653.webp',
    description: 'IT solutions designed specifically for dental practices and oral health clinics.',
    intro: 'Modern dental practices rely heavily on technology - from digital imaging to practice management software. King of IT provides comprehensive IT support tailored to the unique needs of dental offices.',
    challenges: [
      'Integrating digital imaging systems (X-rays, intraoral cameras)',
      'Practice management software support',
      'Patient data security and privacy compliance',
      'Insurance billing system integration',
      'Multi-location practice coordination',
      'Staff training on dental software'
    ],
    solutions: [
      'Dental practice management software support (Dentrix, Eaglesoft, etc.)',
      'Digital imaging system integration',
      'Secure patient data backup',
      'Insurance claim system optimization',
      'Network setup for multi-operatory offices',
      'Staff technology training'
    ],
    compliance: ['PHIPA', 'RCDSO requirements', 'Privacy regulations'],
    cta: 'Modernize your dental practice with reliable IT support.'
  },
  'accounting-firms': {
    title: 'Accounting Firms',
    icon: Calculator,
    heroImage: '/images/optimized/pexels-6863183.webp',
    description: 'Secure IT infrastructure for accounting firms and financial services.',
    intro: 'Accounting firms handle sensitive financial data and must maintain strict confidentiality. King of IT provides secure, reliable IT solutions that meet the demanding requirements of financial professionals.',
    challenges: [
      'Protecting sensitive financial client data',
      'Tax season system performance and reliability',
      'Secure file sharing with clients',
      'Supporting accounting software (QuickBooks, Sage, etc.)',
      'CRA and regulatory compliance',
      'Remote work security for accountants'
    ],
    solutions: [
      'Secure client portal implementation',
      'Accounting software support and optimization',
      'Tax season infrastructure scaling',
      'Encrypted file transfer systems',
      'Audit trail and logging systems',
      'Secure remote access solutions'
    ],
    compliance: ['CPA Ontario requirements', 'CRA data handling', 'Privacy regulations'],
    cta: 'Secure your clients\' financial data with professional IT support.'
  },
  'manufacturing': {
    title: 'Manufacturing',
    icon: Factory,
    heroImage: '/images/optimized/pexels-1108101.webp',
    description: 'Industrial IT solutions for manufacturing and production facilities.',
    intro: 'Manufacturing operations depend on technology for everything from production control to inventory management. King of IT provides robust IT infrastructure that keeps your production lines running.',
    challenges: [
      'Maintaining uptime for production systems',
      'Integrating legacy equipment with modern networks',
      'ERP and inventory management systems',
      'Securing industrial control systems',
      'Multi-site coordination and communication',
      'Compliance with industry standards'
    ],
    solutions: [
      'Production system monitoring and support',
      'ERP implementation and support',
      'Industrial network design and security',
      'Legacy system integration',
      'Multi-facility network connectivity',
      'Disaster recovery for critical systems'
    ],
    compliance: ['Industry 4.0 standards', 'ISO requirements', 'Safety regulations'],
    cta: 'Keep your production running with industrial-strength IT support.'
  },
  'retail': {
    title: 'Retail',
    icon: ShoppingBag,
    heroImage: '/images/optimized/pexels-3965545.webp',
    description: 'IT solutions for retail stores, e-commerce, and omnichannel businesses.',
    intro: 'Retail businesses need reliable technology for point-of-sale, inventory, and customer management. King of IT helps retailers stay competitive with modern, secure IT solutions.',
    challenges: [
      'POS system reliability and security',
      'PCI DSS compliance for payment processing',
      'Inventory management across locations',
      'E-commerce platform integration',
      'Customer data protection',
      'Seasonal scaling requirements'
    ],
    solutions: [
      'POS system setup and support',
      'PCI DSS compliance assistance',
      'Inventory management system integration',
      'E-commerce platform support',
      'Multi-location network management',
      'Customer WiFi solutions'
    ],
    compliance: ['PCI DSS', 'Consumer privacy regulations', 'Payment card standards'],
    cta: 'Power your retail success with reliable IT infrastructure.'
  },
  'real-estate': {
    title: 'Real Estate',
    icon: Home,
    heroImage: '/images/optimized/pexels-7578939.webp',
    description: 'Technology solutions for real estate agencies and property management.',
    intro: 'Real estate professionals need mobile, flexible technology to serve clients anywhere. King of IT provides IT solutions that help agents and brokers stay connected and productive.',
    challenges: [
      'Mobile access to listings and client data',
      'MLS system integration and support',
      'Secure document handling for transactions',
      'Virtual tour and digital marketing tools',
      'Multi-agent office coordination',
      'Client communication systems'
    ],
    solutions: [
      'Mobile device management for agents',
      'MLS integration and support',
      'Secure document management',
      'Virtual tour technology support',
      'CRM system implementation',
      'Cloud-based collaboration tools'
    ],
    compliance: ['RECO requirements', 'Privacy regulations', 'Anti-spam legislation'],
    cta: 'Close more deals with technology that works as hard as you do.'
  },
  'non-profits': {
    title: 'Non-Profits',
    icon: Heart,
    heroImage: '/images/optimized/pexels-6646918.webp',
    description: 'Cost-effective IT solutions for non-profit organizations and charities.',
    intro: 'Non-profit organizations need to maximize every dollar while maintaining professional operations. King of IT provides affordable IT solutions that help non-profits focus on their mission.',
    challenges: [
      'Limited IT budgets',
      'Donor and volunteer data management',
      'Grant reporting and compliance',
      'Fundraising platform integration',
      'Remote volunteer coordination',
      'Maintaining donor trust and data security'
    ],
    solutions: [
      'Non-profit software discounts and grants',
      'Donor management system support',
      'Cloud solutions for cost efficiency',
      'Volunteer coordination tools',
      'Grant compliance assistance',
      'Cybersecurity on a budget'
    ],
    compliance: ['CRA charity requirements', 'Privacy regulations', 'Accessibility standards'],
    cta: 'Stretch your IT budget further with non-profit specialized support.'
  },
  'education': {
    title: 'Education',
    icon: GraduationCap,
    heroImage: '/images/optimized/pexels-5212345.webp',
    description: 'IT services for schools, training centers, and educational institutions.',
    intro: 'Educational institutions face unique technology challenges from classroom management to student data protection. King of IT provides IT solutions that support learning environments.',
    challenges: [
      'Student information system management',
      'Classroom technology support',
      'E-learning platform implementation',
      'Student and staff data protection',
      'BYOD policy management',
      'Content filtering and security'
    ],
    solutions: [
      'Student information system support',
      'Classroom technology setup and support',
      'Learning management system implementation',
      'Network security for educational environments',
      'Device management for students and staff',
      'E-learning platform support'
    ],
    compliance: ['FIPPA', 'Student privacy requirements', 'Accessibility standards'],
    cta: 'Create better learning environments with reliable technology.'
  },
  'government': {
    title: 'Government',
    icon: Landmark,
    heroImage: '/images/optimized/pexels-1550337.webp',
    description: 'Secure IT solutions for government agencies and public sector organizations.',
    intro: 'Government organizations require the highest levels of security and compliance. King of IT provides IT solutions that meet the stringent requirements of public sector operations.',
    challenges: [
      'Strict security and compliance requirements',
      'Legacy system modernization',
      'Public records management',
      'Citizen service portals',
      'Inter-agency communication',
      'Accessibility requirements'
    ],
    solutions: [
      'Government-grade security implementation',
      'Legacy system integration and modernization',
      'Records management systems',
      'Citizen portal development support',
      'Secure inter-agency networking',
      'AODA compliance solutions'
    ],
    compliance: ['FIPPA', 'MFIPPA', 'AODA', 'Government security standards'],
    cta: 'Meet your public service mandate with secure, reliable IT.'
  }
};

const IndustryPage = () => {
  const { industry } = useParams();
  
  // Check if this industry should redirect to a new dedicated landing page
  if (INDUSTRY_REDIRECTS[industry]) {
    return <Navigate to={INDUSTRY_REDIRECTS[industry]} replace />;
  }
  
  const data = industryData[industry];

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Industry not found</h1>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = data.icon;

  return (
    <>
      <SEOHead
        title={`${data.title} IT Services | King of IT Toronto`}
        description={`${data.description} Serving ${data.title.toLowerCase()} in Toronto and the GTA. Call 647-951-3653.`}
        path={`/industries/${industry}`}
      />

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-navy text-white py-16 lg:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={data.heroImage} 
              alt={data.title}
              className="w-full h-full object-cover opacity-20"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/95 to-secondary/80" />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">
                <Building className="w-4 h-4 mr-2" />
                Industry Solutions
              </Badge>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-xl bg-gradient-gold flex items-center justify-center shadow-gold">
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl">
                  IT Solutions for <span className="text-gradient-gold">{data.title}</span>
                </h1>
              </div>
              <p className="text-lg sm:text-xl text-gray-300 mb-8">
                {data.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="w-full sm:w-auto bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
                    Get a Free Consultation
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="tel:6479513653">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10">
                    <Phone className="w-5 h-5 mr-2" />
                    647-951-3653
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-foreground mb-6">
                Understanding Your <span className="text-gradient-gold">Technology Needs</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {data.intro}
              </p>
            </div>
          </div>
        </section>

        {/* Challenges & Solutions */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Challenges */}
              <Card className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-foreground">Common Challenges</h3>
                </div>
                <p className="text-muted-foreground mb-6">
                  {data.title} face unique IT challenges that require specialized solutions:
                </p>
                <ul className="space-y-3">
                  {data.challenges.map((challenge, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-red-600 text-xs font-bold">{index + 1}</span>
                      </div>
                      <span className="text-foreground text-sm">{challenge}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* Solutions */}
              <Card className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-foreground">Our Solutions</h3>
                </div>
                <p className="text-muted-foreground mb-6">
                  King of IT provides comprehensive solutions tailored for {data.title.toLowerCase()}:
                </p>
                <ul className="space-y-3">
                  {data.solutions.map((solution, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-foreground text-sm">{solution}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* Compliance */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                <FileText className="w-3 h-3 mr-1" />
                Compliance
              </Badge>
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-foreground">
                Regulatory <span className="text-gradient-gold">Compliance Support</span>
              </h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                We help {data.title.toLowerCase()} meet their industry-specific compliance requirements.
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4">
              {data.compliance.map((item, index) => (
                <Card key={index} className="px-6 py-4 flex items-center gap-3">
                  <Lock className="w-5 h-5 text-primary" />
                  <span className="font-medium text-foreground">{item}</span>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-4 gap-6">
              <Card className="p-6 text-center">
                <Clock className="w-10 h-10 text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground mb-2">Fast Response</h4>
                <p className="text-muted-foreground text-sm">24/7 emergency support when you need it most</p>
              </Card>
              <Card className="p-6 text-center">
                <Users className="w-10 h-10 text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground mb-2">Industry Expertise</h4>
                <p className="text-muted-foreground text-sm">We understand {data.title.toLowerCase()} IT needs</p>
              </Card>
              <Card className="p-6 text-center">
                <Server className="w-10 h-10 text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground mb-2">Reliable Systems</h4>
                <p className="text-muted-foreground text-sm">Keep your operations running smoothly</p>
              </Card>
              <Card className="p-6 text-center">
                <Cloud className="w-10 h-10 text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground mb-2">Modern Solutions</h4>
                <p className="text-muted-foreground text-sm">Cloud-ready and future-proof</p>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-24 bg-gradient-navy text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <IconComponent className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl mb-4">
              Ready to Improve Your <span className="text-gradient-gold">{data.title}</span> IT?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              {data.cta}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="w-full sm:w-auto bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
                  Get Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="tel:6479513653">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10">
                  <Phone className="w-5 h-5 mr-2" />
                  647-951-3653
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Other Industries */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="font-display font-bold text-xl text-foreground text-center mb-8">
              We Also Serve
            </h3>
            <div className="flex flex-wrap justify-center gap-3">
              {Object.entries(industryData)
                .filter(([key]) => key !== industry)
                .map(([key, ind]) => (
                  <Link key={key} to={`/industries/${key}`}>
                    <Badge 
                      variant="outline" 
                      className="px-4 py-2 text-sm border-border hover:border-primary hover:text-primary transition-colors cursor-pointer"
                    >
                      {ind.title}
                    </Badge>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default IndustryPage;
